import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { 
  Loader2,
  AlertCircle,
  Download,
  FileText,
  Building2,
  Users,
  Mail,
  Phone,
  MapPin,
  Briefcase
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export default function PublicDepartmentPage() {
  const { departmentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const directoryRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [departmentId]);

  const fetchData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/department/${departmentId}/directory`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Département non trouvé');
        } else {
          setError('Erreur lors du chargement');
        }
        return;
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError('Impossible de charger le trombinoscope');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (employee) => {
    return `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();
  };

  const handleExportPDF = async () => {
    if (!directoryRef.current) return;
    setExporting(true);
    
    try {
      const element = directoryRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true
      });
      
      const imgWidth = 297; // A4 landscape width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF({
        orientation: imgHeight > 210 ? 'portrait' : 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Si la hauteur dépasse une page, ajuster
      const pageHeight = pdf.internal.pageSize.getHeight();
      if (imgHeight > pageHeight) {
        const ratio = pageHeight / imgHeight;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth * ratio, pageHeight);
      } else {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      }
      
      const deptName = data?.department?.name?.replace(/\s+/g, '_') || 'departement';
      pdf.save(`trombinoscope_${deptName}_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Colors from branding
  const primaryColor = data?.company?.primaryColor || '#2054A6';
  const accentColor = data?.company?.accentColor || '#F97316';

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: `${primaryColor}08` }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h1 className="text-xl font-semibold text-slate-700 mb-2">{error}</h1>
            <p className="text-slate-500">
              Ce trombinoscope n'est pas disponible.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-6 px-4"
      style={{ backgroundColor: `${primaryColor}05` }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            {data?.company?.logoUrl && (
              <img 
                src={data.company.logoUrl} 
                alt={data.company.name}
                className="h-12 object-contain"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Trombinoscope
              </h1>
              <p className="text-slate-600 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {data?.department?.name}
                <span className="text-slate-400">•</span>
                <Users className="w-4 h-4" />
                {data?.count} collaborateur{data?.count > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleExportPDF}
            disabled={exporting}
            style={{ backgroundColor: primaryColor }}
            className="text-white"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger PDF
          </Button>
        </div>

        {/* Trombinoscope Content */}
        <div ref={directoryRef} className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header Band */}
          <div 
            className="p-6"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="flex items-center justify-between">
              {data?.company?.logoUrl ? (
                <img 
                  src={data.company.logoUrl} 
                  alt="Logo" 
                  className="h-10 object-contain"
                />
              ) : (
                <h2 className="text-white font-bold text-xl">
                  {data?.company?.name}
                </h2>
              )}
              <div className="text-white text-right">
                <h2 className="text-xl font-bold uppercase tracking-wider">
                  Trombinoscope
                </h2>
                <p className="text-white/80">
                  {data?.department?.name}
                </p>
              </div>
            </div>
            {/* Accent bar */}
            <div 
              className="h-1 mt-4 rounded-full"
              style={{ backgroundColor: accentColor }}
            />
          </div>

          {/* Employee Grid */}
          <div className="p-6">
            {data?.employees?.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Aucun collaborateur dans ce département</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {data?.employees?.map((employee) => (
                  <div 
                    key={employee.id}
                    className="rounded-xl overflow-hidden shadow-md border-2 hover:shadow-lg transition-shadow"
                    style={{ borderColor: `${primaryColor}20` }}
                  >
                    {/* Photo */}
                    <div className="bg-slate-50 p-3 flex justify-center">
                      <Avatar 
                        className="h-20 w-20 ring-2 ring-white shadow"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {employee.photoUrl ? (
                          <AvatarImage src={employee.photoUrl} alt={`${employee.firstName} ${employee.lastName}`} />
                        ) : null}
                        <AvatarFallback 
                          className="text-white text-xl font-bold"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {getInitials(employee)}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    
                    {/* Info */}
                    <div 
                      className="p-3 text-center text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <h3 className="font-bold text-sm uppercase">
                        {employee.firstName} {employee.lastName}
                      </h3>
                      <p className="text-white/80 text-xs mt-0.5">
                        {employee.jobTitle}
                      </p>
                      
                      {/* Société et Site */}
                      {(employee.societe || employee.site) && (
                        <div className="flex flex-col items-center gap-0.5 mt-1.5 text-white/70 text-[10px]">
                          {employee.societe && (
                            <div className="flex items-center gap-1">
                              <Briefcase className="w-2.5 h-2.5" />
                              <span>{employee.societe}</span>
                            </div>
                          )}
                          {employee.site && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-2.5 h-2.5" />
                              <span>{employee.site}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Contact icons */}
                      <div className="flex justify-center gap-2 mt-2">
                        {employee.email && (
                          <a 
                            href={`mailto:${employee.email}`}
                            className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                            title={employee.email}
                          >
                            <Mail className="w-3 h-3" />
                          </a>
                        )}
                        {employee.phone && (
                          <a 
                            href={`tel:${employee.phone}`}
                            className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                            title={employee.phone}
                          >
                            <Phone className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      
                      {/* Accent bar */}
                      <div 
                        className="h-1 -mx-3 -mb-3 mt-2"
                        style={{ backgroundColor: accentColor }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div 
            className="py-3 px-6 text-center border-t"
            style={{ borderColor: `${primaryColor}20` }}
          >
            <p className="text-sm text-slate-500">
              {data?.company?.name} • {data?.count} collaborateur{data?.count > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-4">
          Généré via QR Code • {new Date().toLocaleDateString('fr-FR')}
        </p>
      </div>
    </div>
  );
}
